import React, { useRef, useState } from 'react';
import { FormOwnershipModel, FormOwnershipRule, isOwnershipProperty } from 'common/lib/model/form/FormOwnership';
import Button from 'src/components/Button';
import styles from './styles.module.scss';

export type DraftRule = Omit<FormOwnershipRule, 'indexes'> & { indexes?: 'all' | string };

export default function OwnershipRuleEditor({
    model,
    rule,
    onChange,
    onRemove,
}: {
    model: FormOwnershipModel;
    rule: DraftRule;
    onChange: (rule: DraftRule) => void;
    onRemove: () => void;
}) {
    const [expanded, setExpanded] = useState<'indexes' | 'condition' | null>(null);
    const trigger = useRef<HTMLElement | null>(null);
    const properties = model.properties.filter(isOwnershipProperty);
    const property = properties.find((item) => item.name === rule.property);
    const specificIndexes = rule.indexes !== undefined && rule.indexes !== 'all';
    const conditionProperty = properties.find((item) => item.name === rule.where?.property);
    const conditionSummary = rule.where
        ? `${conditionProperty?.title || rule.where.property || 'Choose property'} = ${rule.where.equals || '…'}`
        : '+ Add condition';
    const toggle = (panel: typeof expanded, button: HTMLElement) => {
        trigger.current = button;
        setExpanded(expanded === panel ? null : panel);
    };
    const close = () => {
        setExpanded(null);
        trigger.current?.focus();
    };
    return (
        <>
            <tr className={styles.rule}>
                <td>
                    <label>
                        <span className={styles.srOnly}>Property</span>
                        <select
                            value={rule.property}
                            onChange={(event) => {
                                setExpanded(null);
                                onChange({ property: event.target.value, identity: rule.identity });
                            }}
                        >
                            <option value="">Choose a property…</option>
                            {properties.map((item) => (
                                <option key={item.name} value={item.name}>
                                    {item.title || item.name} ({item.name})
                                </option>
                            ))}
                        </select>
                    </label>
                </td>
                <td>
                    <label>
                        <span className={styles.srOnly}>Contains</span>
                        <select
                            value={rule.identity}
                            onChange={(event) =>
                                onChange({ ...rule, identity: event.target.value as FormOwnershipRule['identity'] })
                            }
                        >
                            <option value="npi">NPI</option>
                            <option value="providerId">Provider ID</option>
                        </select>
                    </label>
                </td>
                <td>
                    {property?.isRepeatable ? (
                        <Button
                            className={styles.optionButton}
                            preventDouble={false}
                            type="transparent"
                            text={specificIndexes ? rule.indexes || 'Set indexes…' : 'All indexes'}
                            title={
                                specificIndexes ? `Indexes: ${rule.indexes || 'not set'}` : 'Use all repeating entries'
                            }
                            aria-label={`Edit indexes for ${property.title || property.name}`}
                            aria-expanded={expanded === 'indexes'}
                            onClick={(event) => toggle('indexes', event.currentTarget)}
                        />
                    ) : (
                        <span className={styles.notRepeatable} title="This property does not repeat">
                            —<span className={styles.srOnly}>Not repeatable</span>
                        </span>
                    )}
                </td>
                <td>
                    {property && (
                        <Button
                            className={styles.optionButton}
                            preventDouble={false}
                            type="transparent"
                            text={rule.where ? `Equals ${rule.where.equals || '…'}` : '+ Add condition'}
                            title={conditionSummary}
                            aria-label={`${rule.where ? 'Edit' : 'Add'} condition for ${
                                property.title || property.name
                            }`}
                            aria-expanded={expanded === 'condition'}
                            onClick={(event) => toggle('condition', event.currentTarget)}
                        />
                    )}
                </td>
                <td>
                    <Button
                        className={styles.remove}
                        type="transparent"
                        text="×"
                        aria-label={`Remove property ${property?.title || rule.property || 'row'}`}
                        onClick={onRemove}
                    />
                </td>
            </tr>
            {expanded && (
                <tr className={styles.detailRow}>
                    <td colSpan={5}>
                        <div className={styles.details}>
                            {expanded === 'indexes' && property?.isRepeatable && (
                                <>
                                    <label>
                                        <span className={styles.srOnly}>Repeatable indexes</span>
                                        <select
                                            value={specificIndexes ? 'specific' : 'all'}
                                            onChange={(event) =>
                                                onChange({
                                                    ...rule,
                                                    indexes: event.target.value === 'all' ? 'all' : '',
                                                })
                                            }
                                        >
                                            <option value="all">All indexes</option>
                                            <option value="specific">Specific indexes</option>
                                        </select>
                                    </label>
                                    {specificIndexes && (
                                        <label>
                                            <span className={styles.srOnly}>Index numbers</span>
                                            <input
                                                value={rule.indexes}
                                                placeholder="e.g. 1, 3, 4"
                                                onChange={(event) => onChange({ ...rule, indexes: event.target.value })}
                                            />
                                        </label>
                                    )}
                                    <span className={styles.detailHint}>Starts at 1; applies on every page.</span>
                                </>
                            )}
                            {expanded === 'condition' && (
                                <>
                                    <span>Only when</span>
                                    <label className={styles.conditionProperty}>
                                        <span className={styles.srOnly}>Only when</span>
                                        <select
                                            value={rule.where?.property || ''}
                                            onChange={(event) =>
                                                onChange({
                                                    ...rule,
                                                    where: event.target.value
                                                        ? {
                                                              property: event.target.value,
                                                              equals: rule.where?.equals || '',
                                                          }
                                                        : undefined,
                                                })
                                            }
                                        >
                                            <option value="">Choose a property…</option>
                                            {properties
                                                .filter((item) => item.isRepeatable === property?.isRepeatable)
                                                .map((item) => (
                                                    <option key={item.name} value={item.name}>
                                                        {item.title || item.name} ({item.name})
                                                    </option>
                                                ))}
                                        </select>
                                    </label>
                                    {rule.where && (
                                        <>
                                            <span>=</span>
                                            <label>
                                                <span className={styles.srOnly}>Equals</span>
                                                <input
                                                    value={rule.where.equals}
                                                    placeholder="Value"
                                                    onChange={(event) =>
                                                        onChange({
                                                            ...rule,
                                                            where: { ...rule.where, equals: event.target.value },
                                                        })
                                                    }
                                                />
                                            </label>
                                            <Button
                                                className={styles.textButton}
                                                type="transparent"
                                                text="Remove condition"
                                                onClick={() => {
                                                    onChange({ ...rule, where: undefined });
                                                    setExpanded(null);
                                                }}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                            <Button
                                className={styles.textButton}
                                type="transparent"
                                text="Done"
                                preventDouble={false}
                                onClick={close}
                            />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
